/**
 * Collect data from hidden fields
 * @returns {Array}
 */
const getData = (settings, callback) => {
  const dataAttribute = settings.dataAttribute;
  const contentValues = settings.availableDataValues;
  const singleDropdownList = ['dome', 'metalearhooks'];
  const isSingleDropdown = singleDropdownList.includes(contentValues[0]);
  // Create new data
  const contentItems = Array.from(
    settings.elem.querySelectorAll('.configurator__accessories-item'),
  );

  if (settings.availableDataValues.length === 0) return;
  const dataValues = {
    contentValues,
    dataAttribute,
    isSingleDropdown,
  };
  const contentLabel = getLabelAndQuantity(contentItems, dataValues);
  const contentImage = getImage(settings.elem, dataValues);
  const contentInput = getInputField(settings.elem, dataValues);
  const contentWrapper = {
    attrValues: contentValues,
    label: contentLabel,
    image: contentImage,
    input: contentInput,
  };
  const { contentList, selectedItems } = mergeContentData(contentWrapper);
  const responseData = {
    data: contentList,
    bundle: contentList[0].isBundle,
    selectedItems: selectedItems,
    accessoriesElements: settings.accessoriesElements ?? [],
  };

  return callback(responseData);
};

/**
 * Get Data for dropdown field
 * @param {Element} contentAccessory
 * @returns {Array}
 */
const getDataValues = (contentAccessory, dataAttribute) => {
  const contentItems = Array.from(
    contentAccessory.querySelectorAll('.configurator__accessories-item'),
  );
  // If no sides is available
  if (!dataAttribute) return [];
  // Get side attribute
  const attrValues = contentItems
    .map((item) => item.getAttribute(dataAttribute))
    .filter((item) => item)
    .map((item) => item.toLowerCase());
  return [...new Set(attrValues)];
};

/**
 * The side attr is coming from Navision and might change in future
 * Ex.: data-filter-****
 * @param {Element} contentAccessory
 * @param {Array} listOfAttributes
 * @returns
 */
const getDataAttribute = (contentAccessory, listOfAttributes) => {
  const contentItems = Array.from(
    contentAccessory.querySelectorAll('.configurator__accessories-item'),
  );
  for (let attr of listOfAttributes) {
    for (let item of contentItems) {
      if (item.getAttribute(attr)) {
        return attr;
      }
    }
  }
};

/**
 * get content for select option field
 * @param {Element} listItems
 * @param {Object} dataValues
 * @returns {Object}
 */
const getLabelAndQuantity = (listItems, dataValues) => {
  const getContentSide = (inputSide) => {
    let items = listItems;
    if (!dataValues.isSingleDropdown) {
      items = listItems.filter((item) => {
        const itemSide = item.getAttribute(dataValues.dataAttribute)?.toLowerCase();
        if (itemSide === inputSide) {
          return item;
        }
      });
    }

    return items.map((item) => {
      const titleElement = item.querySelector('.configurator__accessory-title');
      const skuElement = item.querySelector('.configurator__accessory-sku');
      const fieldInput = item.querySelector('.configurator__accessory-input input');
      return {
        selected: +fieldInput.value || fieldInput.checked,
        inputID: fieldInput.id,
        label: titleElement.innerText,
        sku: skuElement.innerText ?? '',
      };
    });
  };

  let contents = {};
  for (let value of dataValues.contentValues) {
    const newValue = value.toLowerCase();
    contents[newValue] = getContentSide(newValue);
  }
  return contents;
};

/**
 * Get content images
 * @param {Element} accessoryElem
 * @param {Object} dataValues
 * @returns {Object}
 */
const getImage = (accessoryElem, dataValues) => {
  let imageObj = {};
  for (let val of dataValues.contentValues) {
    const imageElement = dataValues.isSingleDropdown
      ? accessoryElem.querySelector(`.accessoryImage`)
      : accessoryElem.querySelector(`[${dataValues.dataAttribute}="${val}" i] .accessoryImage`);
    imageObj[val.toLowerCase()] = imageElement?.src;
  }
  return imageObj;
};

/**
 * Get input type (quantity / bundle)
 * @param {Element} accessoryElem
 * @param {Object} dataValues
 * @returns {Object}
 */
const getInputField = (accessoryElem, dataValues) => {
  let isBundle = false;
  let bundleObj = {};
  for (let val of dataValues.contentValues) {
    const fieldInput = dataValues.isSingleDropdown
      ? accessoryElem.querySelector(`.configurator__accessory-input input`)
      : accessoryElem.querySelector(
          `[${dataValues.dataAttribute}="${val}" i] .configurator__accessory-input input`,
        );
    if (fieldInput.getAttribute('type') == 'checkbox') {
      isBundle = true;
    }
    bundleObj[val.toLowerCase()] = isBundle;
  }
  return bundleObj;
};

/**
 * Construct new data structure
 * @param {Object} contentData
 * @returns
 */
const mergeContentData = (contentData) => {
  const [contentOptions, selectedItems] = selectedContent(
    contentData.attrValues,
    contentData.label,
  );
  const contentList = [];
  for (let item of contentData.attrValues) {
    const key = `${item.toLowerCase()}`;
    const contentItem = {
      attrValues: key,
      isBundle: contentData.input[key],
      image: contentData.image[key],
      data: contentOptions[key],
    };
    contentList.push(contentItem);
  }
  return { contentList, selectedItems };
};

/**
 * Clone data for each selected items
 * @param {String} sides
 * @param {Object} labelData
 * @returns {Object}
 */
const selectedContent = (attrValues, labelData) => {
  const selectedItems = [];
  let newLabels = {};
  let cloneData = JSON.parse(JSON.stringify(labelData));
  for (let val of attrValues) {
    const key = `${val.toLowerCase()}`;
    newLabels[key] = [];
    // Check number of selected items
    const numberOfSelected = cloneData[key]
      .map((obj, index) => {
        return {
          index: index,
          item: obj.selected,
          id: obj.inputID,
        };
      })
      .filter((obj) => obj.item)
      .map((obj) => {
        selectedItems.push(obj.id);
        return obj.index;
      });

    // Duplicate data x number (based of selected items)
    if (numberOfSelected.length > 1) {
      for (let val of numberOfSelected) {
        const duplicateData = JSON.parse(JSON.stringify(labelData));
        const newData = duplicateData[key].map((item, index) => {
          if (index !== val) {
            item.selected = false;
          }
          return item;
        });
        newLabels[key].push(newData);
      }
    } else {
      newLabels[key].push(cloneData[key]);
    }
  }
  return [newLabels, selectedItems];
};

/**
 * Get accessories element
 * @param {Element} accessoryElem
 * @param {String} dataAttribute
 * @returns {Object}
 */
const getAccessories = (accessoryElem, dataAttribute) => {
  const items = Array.from(accessoryElem.querySelectorAll('.configurator__accessories-item'));
  return items.filter((item) => {
    if (item.getAttribute(dataAttribute)) {
      return false;
    }

    return true;
  });
};

export { getData, getDataAttribute, getDataValues, getAccessories };
